import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWhatsNew, getMonthSlug } from '../http-hooks/whats-new/useWhatsNew';
import { C } from '../styles/tokens';
import { useGlobalStore } from '../zustand-store/useGlobalStore';

const SEEN_KEY = 'l2it_whats_new_last_seen_slug';

const TYPE_CONFIG = {
  improvement: { label: 'Improvement', bg: C.p100, color: C.p700 },
  fix: { label: 'Bug Fix', bg: C.g50, color: C.g600 },
  feature: { label: 'New', bg: C.v50, color: '#6D28D9' },
};

function markRead() {
  localStorage.setItem(SEEN_KEY, getMonthSlug());
}

// ── Centered modal — dark overlay, boxed dialog, matching the reference WhatsNewModal ──
function WhatsNewPanel({ data, loading, error, onClose }) {
  const bodyRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 640, maxWidth: '100%', maxHeight: '85vh',
          background: C.w, borderRadius: 12, boxShadow: '0 30px 80px rgba(0,0,0,.35)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Sticky header */}
        <div style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px',
          borderBottom: `1px solid ${scrolled ? C.n200 : 'transparent'}`,
          transition: 'border-color .15s',
        }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: C.n800, letterSpacing: '-.01em' }}>What's new?</span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.n500 }}
            onMouseEnter={e => { e.currentTarget.style.background = C.n100; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div
          ref={bodyRef}
          onScroll={e => setScrolled(e.currentTarget.scrollTop > 4)}
          style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 32px' }}
        >

          {loading && !data && (
            <div style={{ textAlign: 'center', color: C.n400, fontSize: 13, padding: '48px 0' }}>
              Loading…
            </div>
          )}

          {loading && data && (
            <div style={{ textAlign: 'right', color: C.n400, fontSize: 11, marginBottom: 10 }}>
              Refreshing…
            </div>
          )}

          {!loading && !data && error && (
            <div style={{ textAlign: 'center', color: C.n400, fontSize: 13, padding: '48px 0' }}>
              Couldn't load updates — check back soon.
            </div>
          )}

          {!loading && !data && !error && (
            <div style={{ textAlign: 'center', color: C.n400, fontSize: 13, padding: '48px 0' }}>
              No updates yet — check back soon.
            </div>
          )}

          {data && (
            <>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: C.n900, letterSpacing: '-.02em', margin: '12px 0 20px' }}>
                {data.title}
              </h2>

              {data.items.length === 0 && (
                <div style={{ textAlign: 'center', color: C.n400, fontSize: 13, padding: '16px 0' }}>
                  No updates yet — check back soon.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {data.items.map((item, i) => {
                  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.improvement;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <span style={{
                          flexShrink: 0,
                          padding: '2px 8px', borderRadius: 100,
                          background: cfg.bg, color: cfg.color,
                          fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
                        }}>
                          {cfg.label}
                        </span>
                        <span style={{ fontSize: 15.5, fontWeight: 700, color: C.n900 }}>
                          {item.title}
                        </span>
                      </div>

                      {item.description && (
                        <p style={{ margin: '0 0 12px', fontSize: 13.5, color: C.n600, lineHeight: 1.6 }}>
                          {item.description}
                        </p>
                      )}

                      {item.screenshotUrl && (
                        <img
                          src={item.screenshotUrl}
                          alt={item.title}
                          style={{
                            width: '100%', height: 'auto', display: 'block',
                            borderRadius: 8, border: `1px solid ${C.n200}`,
                            boxShadow: '0 2px 8px rgba(0,0,0,.08)',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {data.blogUrl && (
                <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${C.n100}`, display: 'flex', justifyContent: 'flex-end' }}>
                  <a
                    href={data.blogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '9px 16px', borderRadius: 8,
                      background: C.n900, color: C.w,
                      fontSize: 13, fontWeight: 600, textDecoration: 'none',
                    }}
                  >
                    Read full post
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
                    </svg>
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Header trigger button — bordered pill with icon + "What's new" label, matching the reference StudioTopBar ──
export function WhatsNewButton() {
  const showPanel = useGlobalStore(state => state.showWhatsNewPanel);
  const togglePanel = useGlobalStore(state => state.toggleWhatsNewPanel);
  const { data, loading, error } = useWhatsNew();
  const buttonRef = useRef(null);

  const [unread, setUnread] = useState(
    () => localStorage.getItem(SEEN_KEY) !== getMonthSlug()
  );

  useEffect(() => {
    if (showPanel && data) {
      markRead();
      setUnread(false);
    }
  }, [showPanel, data]);

  useEffect(() => () => togglePanel(false), []); // reset if the header unmounts while panel is open

  const handleOpen = () => togglePanel(true);
  const handleClose = () => togglePanel(false);

  return (
    <>
      {/* Desktop — bordered pill with icon + label + dot */}
      <button
        ref={buttonRef}
        type="button"
        onClick={showPanel ? handleClose : handleOpen}
        aria-label="What's New?"
        className="d-none d-md-flex"
        style={{
          position: 'relative', alignItems: 'center', gap: 6,
          padding: '8px 14px', flexShrink: 0,
          border: `1px solid ${C.n200}`, borderRadius: 9,
          background: C.w, color: C.n700,
          cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.n600} strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        What's New?
        {unread && !showPanel && data && (
          <span style={{
            position: 'absolute', top: 6, left: 24, width: 7, height: 7,
            borderRadius: '50%', background: C.r500, boxShadow: `0 0 0 2.5px ${C.w}`,
          }} />
        )}
      </button>

      {/* Mobile — bordered icon-only square */}
      <button
        type="button"
        onClick={showPanel ? handleClose : handleOpen}
        aria-label="What's New?"
        className="d-md-none"
        style={{
          position: 'relative', width: 34, height: 34, borderRadius: 9,
          border: `1px solid ${C.n200}`, background: C.w, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.n700} strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread && !showPanel && data && (
          <span style={{
            position: 'absolute', top: 5, right: 6, width: 7, height: 7,
            borderRadius: '50%', background: C.r500, boxShadow: `0 0 0 2px ${C.w}`,
          }} />
        )}
      </button>

      {showPanel && (
        <WhatsNewPanel
          data={data}
          loading={loading}
          error={error}
          onClose={handleClose}
        />
      )}
    </>
  );
}
